import { BloodGlucoseReading } from "@/model/BloodGlucoseReading";
import { MedicationTakenAnswer } from "@/model/Medication";
import cn from "@/util/cn";
import { DateTimeFormatter } from "@js-joda/core";
import { Locale } from "@js-joda/locale";
import { Cell, CellProps } from "../../Cell";
import NewDataTooltipIndicator from "../../NewDataTooltipIndicator";
import {
  backgroundColorForClassification,
  foregroundColorForClassification,
} from "../colorForClassification";

type OverviewCellProps = {
  reading?: BloodGlucoseReading;
  status: "default" | "new" | "disabled";
  showMedication: boolean;
  medicationTaken?: MedicationTakenAnswer;
} & CellProps;

const timeFormatter = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US);

const OverviewCell = ({
  reading,
  status,
  showMedication,
  medicationTaken,
  className,
  ...otherProps
}: OverviewCellProps) => {
  const isNew = status === "new";
  const isDisabled = status === "disabled";

  const readingBoxColor = isDisabled
    ? "bg-slate-100"
    : !reading
      ? "bg-transparent"
      : backgroundColorForClassification(reading.classification);

  const readingTextColor = !reading
    ? "text-slate-600"
    : foregroundColorForClassification(reading.classification);

  return (
    <Cell
      className={cn(
        "relative flex flex-col items-center justify-center p-2 font-semibold",
        readingBoxColor,
        className
      )}
      outline
      {...otherProps}
    >
      {isNew && (
        <div className="absolute right-2 top-2">
          <NewDataTooltipIndicator />
        </div>
      )}
      {!isDisabled &&
        (reading ? (
          <>
            <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center">
              <p className={cn("col-start-2 text-center", readingTextColor)}>
                {reading.bloodGlucoseInMilligramsPerDeciliter.toString()}
              </p>
              {showMedication && medicationTaken !== undefined && (
                <span
                  className="material-symbols-outlined justify-self-start ps-1 text-gray-400"
                  style={{ fontSize: "18px" }}
                  title={medicationTaken === "YES" ? "Medication taken" : "Medication not taken"}
                >
                  {medicationTaken === "YES" ? "pill" : "pill_off"}
                </span>
              )}
            </div>
            <span className="text-xs font-normal text-slate-500">
              {timeFormatter.format(reading.dateTime)}
            </span>
          </>
        ) : (
          <p className={readingTextColor}>-</p>
        ))}
    </Cell>
  );
};

export default OverviewCell;
